
import { Modal } from "theme";
import { animate } from "vendor";

const STORAGE_KEY = "perfumistta:compare";
const MAX_ITEMS = 2;
const SECTION_ID = "product-compare-item";

const ROW_ORDER = [
  "header",
  "family",
  "sweetness",
  "freshness",
  "intensity",
  "pyramid",
  "concentration",
  "fixation",
  "projection",
  "content",
  "suitable",
  "unsuitable",
  "occasions",
  "buy",
];

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;


const CompareStore = {
  _memory: [],

  get items() {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(items) ? items.slice(0, MAX_ITEMS) : [];
    } catch (error) {
      return this._memory;
    }
  },

  _save(items, detail = {}) {
    this._memory = items;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
    }

    document.dispatchEvent(new CustomEvent("compare:change", { detail: { items, ...detail } }));
  },

  has(handle) {
    return this.items.some((item) => item.handle === handle);
  },

  add(item) {
    if (this.has(item.handle)) {
      return;
    }

    const items = [...this.items, item].slice(-MAX_ITEMS);
    this._save(items, { added: item.handle });
  },

  replace(oldHandle, item) {
    const items = this.items.map((current) => (current.handle === oldHandle ? item : current));
    this._save(items, { added: item.handle, removed: oldHandle });
  },

  remove(handle) {
    this._save(this.items.filter((item) => item.handle !== handle), { removed: handle });
  },

  clear() {
    this._save([], { cleared: true });
  },
};

window.addEventListener("storage", (event) => {
  if (event.key === STORAGE_KEY) {
    document.dispatchEvent(new CustomEvent("compare:change", { detail: { items: CompareStore.items } }));
  }
});


class CompareToggle extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.input = this.querySelector('input[type="checkbox"]');
    this.input.addEventListener("change", this.#onChange.bind(this), { signal });
    document.addEventListener("compare:change", this.#sync.bind(this), { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get handle() {
    return this.getAttribute("handle");
  }

  #onChange() {
    if (this.input.checked) {
      const item = { handle: this.handle, title: this.dataset.title, image: this.dataset.image };
      const bar = document.querySelector("compare-bar");

      if (CompareStore.items.length >= MAX_ITEMS && bar) {
        this.input.checked = false;
        bar.startReplace(item);
        return;
      }

      CompareStore.add(item);

      if (CompareStore.items.length === MAX_ITEMS) {
        setTimeout(() => document.getElementById("product-compare-modal")?.show());
      }
    } else {
      CompareStore.remove(this.handle);
    }
  }

  #sync() {
    const checked = CompareStore.has(this.handle);
    this.input.checked = checked;
    this.classList.toggle("is-checked", checked);
  }
}


class CompareBar extends HTMLElement {
  connectedCallback() {
    this.slotsContainer = this.querySelector("[data-compare-slots]");
    this.openButton = this.querySelector("[data-compare-open]");
    this.statusElement = this.querySelector("[data-compare-status]");
    this.slotTemplate = this.querySelector("[data-compare-slot-template]");
    this.emptyTemplate = this.querySelector("[data-compare-empty-template]");

    this.openButton.addEventListener("click", () => document.getElementById("product-compare-modal")?.show());
    this.querySelector("[data-compare-clear]").addEventListener("click", () => CompareStore.clear());

    new ResizeObserver(() => {
      document.documentElement.style.setProperty("--compare-bar-height", `${this.offsetHeight}px`);
    }).observe(this);

    this.replacePrompt = this.querySelector("[data-compare-replace]");
    this.querySelector("[data-compare-replace-cancel]").addEventListener("click", () => this.cancelReplace());

    this.addEventListener("click", (event) => {
      if (this.pendingItem) {
        const slot = event.target.closest(".compare-bar__slot[data-handle]");

        if (slot) {
          this.#confirmReplace(slot.dataset.handle);
        }

        return;
      }

      const removeButton = event.target.closest("[data-compare-remove]");

      if (removeButton) {
        CompareStore.remove(removeButton.closest("[data-handle]").dataset.handle);
      }
    });

    this.addEventListener("keydown", (event) => {
      const slot = event.target.closest(".compare-bar__slot[data-handle]");

      if (this.pendingItem && slot && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        this.#confirmReplace(slot.dataset.handle);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (this.pendingItem && event.key === "Escape") {
        this.cancelReplace();
      }
    });

    document.addEventListener("compare:change", (event) => {
      this.cancelReplace();
      this.#render(event.detail.items, true);
    });
    this.#render(CompareStore.items, true);
  }

  startReplace(item) {
    this.pendingItem = item;
    this.classList.add("is-replacing");
    this.querySelector("[data-compare-replace-name]").textContent = item.title;
    this.replacePrompt.hidden = false;
    this.statusElement.textContent = `Escolha qual perfume substituir por ${item.title}`;

    const slots = Array.from(this.slotsContainer.querySelectorAll(".compare-bar__slot[data-handle]"));

    slots.forEach((slot) => {
      slot.setAttribute("role", "button");
      slot.setAttribute("tabindex", "0");
      slot.setAttribute("aria-label", `Substituir ${slot.querySelector(".compare-bar__name").textContent} por ${item.title}`);
    });

    slots[0]?.focus({ preventScroll: true });

    if (!prefersReducedMotion()) {
      animate(this.replacePrompt, { transform: ["translateY(8px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] });
      slots.forEach((slot, index) => {
        animate(slot, { transform: ["translateY(0)", "translateY(-6px)", "translateY(0)"] }, { duration: 0.4, delay: index * 0.08, easing: "ease-out" });
      });
    }
  }

  cancelReplace() {
    if (!this.pendingItem) {
      return;
    }

    this.pendingItem = null;
    this.classList.remove("is-replacing");
    this.replacePrompt.hidden = true;
    this.statusElement.textContent = "";

    this.slotsContainer.querySelectorAll(".compare-bar__slot[data-handle]").forEach((slot) => {
      slot.removeAttribute("role");
      slot.removeAttribute("tabindex");
      slot.removeAttribute("aria-label");
    });
  }

  #confirmReplace(oldHandle) {
    const item = this.pendingItem;
    this.cancelReplace();
    CompareStore.replace(oldHandle, item);

    setTimeout(() => document.getElementById("product-compare-modal")?.show());
  }

  #render(items, animated) {
    const slots = [];

    items.forEach((item) => {
      const slot = this.slotTemplate.content.firstElementChild.cloneNode(true);
      const image = slot.querySelector("img");

      slot.dataset.handle = item.handle;
      slot.querySelector(".compare-bar__name").textContent = item.title;
      slot.querySelector("[data-compare-remove] .sr-only").textContent = `Remover ${item.title}`;

      if (item.image) {
        image.src = item.image;
      } else {
        image.remove();
      }

      slots.push(slot);
    });

    for (let i = items.length; i < MAX_ITEMS; i++) {
      slots.push(this.emptyTemplate.content.firstElementChild.cloneNode(true));
    }

    this.slotsContainer.replaceChildren(...slots);
    this.openButton.disabled = items.length < MAX_ITEMS;
    this.statusElement.textContent = items.length > 0 ? `${items.length} de ${MAX_ITEMS} perfumes selecionados para comparar` : "";

    this.#setVisible(items.length > 0, animated);
  }

  #setVisible(visible, animated) {
    if (visible === !this.hidden) {
      return;
    }

    document.documentElement.classList.toggle("has-compare-bar", visible);

    if (!animated || prefersReducedMotion()) {
      this.hidden = !visible;
      return;
    }

    if (visible) {
      this.hidden = false;
      animate(this, { transform: ["translateY(100%)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] });
    } else {
      animate(this, { transform: ["translateY(0)", "translateY(100%)"], opacity: [1, 0] }, { duration: 0.25, easing: [0.645, 0.045, 0.355, 1] }).finished.then(() => {
        this.hidden = CompareStore.items.length === 0;
        this.style.removeProperty("transform");
        this.style.removeProperty("opacity");
      });
    }
  }
}


const loadedItems = new Map();

function loadCompareItem(handle) {
  if (!loadedItems.has(handle)) {
    const promise = fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Could not load compare data for ${handle}`);
        }

        return response.text();
      })
      .then((html) => {
        const item = new DOMParser().parseFromString(html, "text/html").querySelector(".product-compare-item");

        if (!item) {
          throw new Error(`Missing compare markup for ${handle}`);
        }

        return item;
      })
      .catch((error) => {
        loadedItems.delete(handle); // permite tentar de novo na próxima abertura
        throw error;
      });

    loadedItems.set(handle, promise);
  }

  return loadedItems.get(handle);
}

class ProductCompareModal extends Modal {
  #renderedKey = null;

  constructor() {
    super();

    document.addEventListener("compare:change", this.#onCompareChange.bind(this));

    if (window.themeVariables.settings.cartType === "drawer") {
      document.addEventListener("variant:add", (event) => {
        if (this.open && this.contains(event.target)) {
          this.hide();
        }
      });
    }

    this.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-compare-remove]");

      if (removeButton) {
        CompareStore.remove(removeButton.dataset.handle);
      }
    });
  }

  get table() {
    return this.querySelector("[data-compare-table]");
  }

  async show(animate = true) {
    const items = CompareStore.items;

    if (items.length < MAX_ITEMS) {
      return;
    }

    const key = items.map((item) => item.handle).join("|");

    if (key !== this.#renderedKey) {
      this.#renderSkeleton(items);
    }

    const showPromise = super.show(animate);

    if (key !== this.#renderedKey) {
      await this.#renderTable(items, key);
    }

    return showPromise;
  }

  #onCompareChange(event) {
    if (!this.open) {
      return;
    }

    if (event.detail.items.length < MAX_ITEMS) {
      this.hide();
    } else {
      this.show();
    }
  }

  #renderSkeleton(items) {
    this.#renderedKey = null;
    this.table.setAttribute("aria-busy", "true");

    const skeleton = document.createElement("div");
    skeleton.className = "product-compare__row product-compare__row--header";
    skeleton.setAttribute("role", "row");
    skeleton.innerHTML = `<div class="product-compare__label" role="rowheader"></div>`;

    items.forEach((item) => {
      const cell = document.createElement("div");
      cell.className = "product-compare__cell";
      cell.setAttribute("role", "cell");
      cell.innerHTML = `
        <div class="product-compare-item__header">
          <span class="product-compare-item__media">${item.image ? `<img class="product-compare-item__image" src="${item.image}" alt="">` : ""}</span>
          <div class="product-compare-item__heading v-stack gap-1"><p class="product-compare-item__title"></p></div>
        </div>
      `;
      cell.querySelector(".product-compare-item__title").textContent = item.title;
      skeleton.appendChild(cell);
    });

    const placeholders = Array.from({ length: 5 }, () => `
      <div class="product-compare__row product-compare__row--skeleton" aria-hidden="true">
        <div class="product-compare__label"><span class="product-compare__skeleton"></span></div>
        <div class="product-compare__cell"><span class="product-compare__skeleton"></span></div>
        <div class="product-compare__cell"><span class="product-compare__skeleton"></span></div>
      </div>
    `).join("");

    const scrollArea = this.#createScrollArea();
    scrollArea.innerHTML = placeholders;

    this.table.replaceChildren(skeleton, scrollArea);
  }

  #createScrollArea() {
    const scrollArea = document.createElement("div");
    scrollArea.className = "product-compare__scroll";
    scrollArea.setAttribute("role", "rowgroup");
    return scrollArea;
  }

  async #renderTable(items, key) {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    const results = await Promise.allSettled(items.map((item) => loadCompareItem(item.handle)));

    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

    if (key !== CompareStore.items.map((item) => item.handle).join("|")) {
      return;
    }

    const columns = results.map((result, index) => {
      const cells = new Map();

      if (result.status === "fulfilled") {
        result.value.querySelectorAll("[data-compare-row]").forEach((cell) => cells.set(cell.dataset.compareRow, cell));
      } else {
        cells.set("header", this.#createErrorCell(items[index]));
      }

      return { item: items[index], cells };
    });

    const fragment = document.createDocumentFragment();
    const scrollArea = this.#createScrollArea();
    let buyRow = null;

    ROW_ORDER.forEach((rowKey) => {
      const rowCells = columns.map((column) => column.cells.get(rowKey));

      if (rowCells.every((cell) => !cell)) {
        return;
      }

      const row = document.createElement("div");
      row.className = `product-compare__row product-compare__row--${rowKey}`;
      row.setAttribute("role", "row");

      const label = document.createElement("div");
      label.className = "product-compare__label smallcaps";
      label.setAttribute("role", "rowheader");
      label.textContent = rowCells.find((cell) => cell?.dataset.compareLabel)?.dataset.compareLabel || "";
      row.appendChild(label);

      const values = rowCells.map((cell) => parseFloat(cell?.dataset.compareValue));
      const highest = Math.max(...values.filter((value) => !isNaN(value)));
      const shouldHighlight = values.every((value) => !isNaN(value)) && new Set(values).size > 1;

      rowCells.forEach((cell, index) => {
        const wrapper = document.createElement("div");
        wrapper.className = "product-compare__cell";
        wrapper.setAttribute("role", "cell");

        if (cell) {
          wrapper.append(...Array.from(cell.childNodes).map((node) => document.importNode(node, true)));
        } else {
          wrapper.innerHTML = `<span class="product-compare__empty" aria-label="Não informado">—</span>`;
        }

        if (shouldHighlight && values[index] === highest) {
          wrapper.classList.add("is-highest");
        }

        if (rowKey === "header") {
          wrapper.appendChild(this.#createRemoveButton(columns[index].item));
        }

        row.appendChild(wrapper);
      });

      if (rowKey === "header") {
        fragment.appendChild(row);
      } else if (rowKey === "buy") {
        buyRow = row;
      } else {
        scrollArea.appendChild(row);
      }
    });

    fragment.appendChild(scrollArea);

    if (buyRow) {
      fragment.appendChild(buyRow);
    }

    this.table.replaceChildren(fragment);
    this.table.removeAttribute("aria-busy");
    this.#renderedKey = key;

    window.jdgm?.customizeBadges?.();
  }

  #createRemoveButton(item) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "product-compare__remove link text-xs text-subdued";
    button.dataset.compareRemove = "";
    button.dataset.handle = item.handle;
    button.textContent = "Remover";
    button.setAttribute("aria-label", `Remover ${item.title} da comparação`);
    return button;
  }

  #createErrorCell(item) {
    const cell = document.createElement("div");
    const link = document.createElement("a");

    link.href = `${window.Shopify.routes.root}products/${item.handle}`;
    link.className = "link";
    link.textContent = item.title;

    cell.innerHTML = `<p class="text-subdued text-sm">Não foi possível carregar este produto.</p>`;
    cell.prepend(link);
    return cell;
  }
}

if (!window.customElements.get("compare-toggle")) {
  window.customElements.define("compare-toggle", CompareToggle);
}

if (!window.customElements.get("compare-bar")) {
  window.customElements.define("compare-bar", CompareBar);
}

if (!window.customElements.get("product-compare-modal")) {
  window.customElements.define("product-compare-modal", ProductCompareModal);
}
